import { Source } from '../models/Source'
import ApiProvider from "./ApiProvider"
import Booking, { BookingStatus } from '../models/Booking'
import Venue from '../models/Venue'
import { TimeAction } from '../services/TimesProvider'
import Offer from '../models/Offer'
import Package from '../models/Package'
import DateService from './DateService'

enum DepositType {
    deposit = 'deposit',
    payment = 'payment',
    authenticate = 'authenticate'
}

enum PreorderPayment {
    percentage = 'percentage',
    fullAmount = 'full_amount'
}

export enum TagType {
    multiselect = 'multiselect',
    select = 'select',
    boolean = 'boolean',
    text = 'text',
    longtext = 'longtext'
}

export interface MarketingPreferenceInterface {
    id: string,
    description: string,
    checked: boolean
}

export class MarketingPreference implements MarketingPreferenceInterface {
    id: string
    description: string
    checked: boolean = false

    constructor(data: any) {
        this.id = data.id
        this.description = data.description
    }
}

export class Tag {
    id: string
    label: string
    options: string[]
    type: TagType
    isDeleted: boolean

    constructor (data: any) {
        this.id = data.id
        this.label = data.label
        this.options = data.options
        this.type = data.type
        this.isDeleted = data.deleted_at !== null
    }

    static shouldDisplay (tag: Tag): boolean {
        return !tag.isDeleted
    }
}

export class BookingDetails {
    guestListNamesRequired: boolean
    guestListGenderRequired: boolean
    dobRequired: boolean
    dobHidden: boolean
    newsletterSignup: boolean

    status: BookingStatus
    action: TimeAction

    accessToken: string

    customFieldLabel: string | null = null
    customFieldValue: string | null = null

    marketingPreferences: MarketingPreference[] = []
    offers: Offer[] = []
    packages: any[] = []
    tagFields: Tag[] = []

    private venueConfig: any
    private bookingOptions: any
    private preorderMenus: any[]
    private booking: any

    constructor (data: any) {
        this.guestListNamesRequired = !!data.bookingOptions.guestListNamesRequired
        this.guestListGenderRequired = !!data.bookingOptions.guestListGenderRequired
        this.dobRequired = !!data.bookingOptions.dobRequired
        this.dobHidden = !!data.bookingOptions.dobHidden
        this.newsletterSignup = !!data.bookingOptions.newsletterSignup

        this.accessToken = data.accessToken

        this.status = data.availability.status
        this.action = data.availability.action

        this.venueConfig = data.venueConfig
        this.bookingOptions = data.bookingOptions
        this.preorderMenus = data.preorderMenus
        this.booking = data.booking

        this.customFieldLabel = data.bookingOptions.customField
        this.customFieldValue = null

        data.offers.forEach((offer: any) => {
            this.offers.push(new Offer(offer))
        })

        if (data.packages) {
            data.packages.forEach((pack: any) => {
                this.packages.push(new Package(pack))
            })
        }

        if (this.hasTagFields) {
            this.venueConfig.bookingTags.forEach((tag: any) => {
                if (Tag.shouldDisplay(tag)) {
                    this.tagFields.push(new Tag(tag))
                }
            })
        }

        if (this.hasMarketingPreferences) {
            this.venueConfig.marketingPreferences.forEach(
                (preference: MarketingPreferenceInterface) => {
                    this.marketingPreferences.push(new MarketingPreference(preference))
                }
            )
        }
    }

    get hasBookingPolicy (): boolean {
        return this.bookingPolicy !== '' && this.bookingPolicy !== undefined
    }

    get bookingPolicy (): string | null {
        return this.bookingOptions.bookingPolicy
    }

    get hasTagFields (): boolean {
        const tags = this.venueConfig.bookingTags

        return tags && tags.length
    }

    get venueName (): string {
        const venueConfig = this.venueConfig

        return venueConfig.title === 'Any' ? venueConfig.venueGroupName : venueConfig.title
    }

    get shouldShowDobField (): boolean {
        return !this.dobHidden
    }

    get shouldShowNewsletterSignup (): boolean {
        return this.newsletterSignup
    }

    get hasCustomField (): boolean {
        return !!this.customFieldLabel
    }

    get hasMarketingPreferences(): boolean {
        return !!this.venueConfig.marketingPreferences
    }

    get shouldConfirm (): boolean {
        return this.action === TimeAction.accept || this.action === TimeAction.confirm
    }

    get hasDepositRequired (): boolean {
        return this.bookingOptions.depositRequired !== undefined
    }

    get depositType (): DepositType | null {
        if (this.hasDepositRequired) {
            return this.bookingOptions.depositRequired.type
        }

        return null
    }

    get depositRequired (): boolean {
        return this.hasDepositRequired && this.depositType === DepositType.deposit
    }

    get paymentRequired (): boolean {
        return this.hasDepositRequired && this.depositType === DepositType.payment
    }

    get authenticationRequired (): boolean {
        return this.hasDepositRequired && this.depositType === DepositType.authenticate
    }

    get cardDetailsRequired (): boolean {
        return this.depositRequired || this.paymentRequired || this.authenticationRequired
    }

    get hasMenus (): boolean {
        return this.preorderMenus && this.preorderMenus.length > 0
    }

    get isGuestList (): boolean {
        return this.guestListGenderRequired || this.guestListNamesRequired
    }

    get shouldCompleteInModal (): boolean {
        return this.cardDetailsRequired || this.hasMenus || this.isGuestList
    }

    get preorderIsRequired (): boolean {
        if (!this.hasMenus) return false

        let preorderRequired = false

        this.preorderMenus.forEach((menu: any) => {
            if (menu.minimum_preorder_amount > 0) {
                preorderRequired = true
                return
            }
        })

        return preorderRequired
    }

    get preorderRequiresPartPayment (): boolean {
        if (!this.hasMenus) return false

        return this.bookingOptions.preorders.paymentRequired === PreorderPayment.percentage
    }

    get preorderRequiresFullPayment (): boolean {
        if (!this.hasMenus) return false

        return this.bookingOptions.preorders.paymentRequired === PreorderPayment.fullAmount
    }

    get preorderRequiresNoPayment (): boolean {
        if (!this.hasMenus) return false

        return !this.preorderRequiresFullPayment && !this.preorderRequiresFullPayment
    }

    get totalAmount (): number | null{
        if (this.cardDetailsRequired) {
            return this.bookingOptions.depositRequired.totalFormatted
        }

        return null
    }

    get shouldContinueToDepositStage () {
        return this.depositRequired && !this.isGuestList
    }

    get preorderAndPaymentMsg (): string {
        switch (true) {
            case this.hasMenus && this.preorderRequiresPartPayment && this.preorderIsRequired:
                return 'You will need to pre-order some items and pay a deposit for your chosen items in order to complete this booking.'
            case this.hasMenus && this.preorderRequiresFullPayment && this.preorderIsRequired:
                return 'You will need to pre-order some items and pay for your chosen items in order to complete this booking.'
            case this.hasMenus && this.preorderRequiresNoPayment && this.preorderIsRequired:
                return 'You will need to pre-order some items in order to complete this booking'
            case this.depositType === DepositType.payment:
                return 'To secure this booking, we will need to take a payment of ' + this.totalAmount
            case this.depositType === DepositType.authenticate:
                return 'To secure this booking, we will need to hold your card details for an amount of <strong>' + this.totalAmount + '</strong>. We hold your card details, but no money will be taken unless you fail to turn up for your booking or break the booking terms.'
            default:
                return '' // return 'To secure this booking, we will need to hold your card details for a deposit of <strong>' + this.getTotalAmount() + '</strong>. We hold your card details, but no money will be taken unless you fail to turn up for your booking or break the booking terms.'
          }
    }
}

export default class BookingDetailsProvider extends ApiProvider {
    booking: Booking

    constructor (booking: Booking) {
        super()

        this.booking = booking
    }

    requestBookingDetails (): Promise<BookingDetails> {
        const requestUrl = this.baseUrl + '/bookings'
        const params = this.booking.toQuery()

        params.action = 'create'

        if (this.urlOptions.source) {
            params.source = this.urlOptions.source
        }

        if (this.urlOptions.affiliateId) {
            params.affiliate_id = this.urlOptions.affiliateId
        }

        return this.post(requestUrl, params)
            .then((response) => {
                const payload = response.data.payload

                if (payload.bookingOptions) {
                    const bookingDetails = new BookingDetails(payload)

                    return bookingDetails
                }

                throw 'booking_unavailable'
            })
    }
}



// WEBPACK FOOTER //
// ./src/services/BookingDetailsProvider.ts